/* Copyright (c) 2019 - 2021. Verimatrix. All Rights Reserved.
   All information in this file is Verimatrix Confidential and Proprietary. */

import React, { useCallback, useEffect, useState } from "react";
import { useApolloClient } from "@apollo/react-hooks";
import { usersByEmail } from "./queries";
import { Button, Form, Segment } from "semantic-ui-react";
import styles from "../MyAccount.module.css";
import cStyles from "./../../Components.module.css";
import { authCheckCallApi, getUrlParam } from "../../Authenticator/utils";
import { MessageDialogParams } from "../../../types/MessageDialogParams";
import useForm from "react-hook-form";
import UsersList from "./UsersList";
import ConfirmationDialog from "../../ConfirmationDialog";
import { Ico } from "../../Icon/Icon";

interface UsersProps {
  ro: boolean;
  setSelectedUser: (user: string | null) => void;
  selectedUser: string | null;
  showSelectedOrg: (org: string) => void;
}

const Users: React.FC<UsersProps> = ({
  ro,
  selectedUser,
  setSelectedUser,
  showSelectedOrg,
}) => {
  const [message, setMessage] = useState<MessageDialogParams | null>(null);
  const client = useApolloClient();
  const { register, handleSubmit, errors, setValue, getValues } =
    useForm<FormData>();
  const [usersData, setUsersData] = useState<any>();
  const [loading, setLoading] = useState<boolean>(false);

  type FormData = {
    username: string;
  };
  const emptyInputs = {
    username: "",
  };
  const [inputs, setInputs] = useState<FormData>(emptyInputs);
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setInputs((inputs) => ({ ...inputs, [name]: value }));
  };

  const searchUsers = useCallback(() => {
    setLoading(true);
    authCheckCallApi(() => {
      client
        .query({
          fetchPolicy: "no-cache",
          query: usersByEmail,
          variables: {
            email: getValues().username.toLocaleLowerCase(),
          },
        })
        .then((result) => {
          setUsersData(result.data);
        })
        .catch((e) => {
          setMessage({
            title: "Search Users",
            message: e.message,
          });
        })
        .finally(() => {
          setLoading(false);
        });
    });
  }, [client, inputs, selectedUser]);

  useEffect(() => {
    const userParam = getUrlParam("u") || selectedUser;
    if (userParam) {
      setValue("username", userParam);
      searchUsers();
      setSelectedUser(null);
    }
  }, [selectedUser]);

  return (
    <Segment basic>
      <Form
        onSubmit={handleSubmit(searchUsers)}
        style={{
          display: "flex",
          alignItems: "baseline",
        }}
      >
        <Form.Group inline className={styles.landingFormGroup}>
          <Form.Field
            required
            error={!!errors.username}
            className={styles.landingFormField}
          >
            <input
              autoComplete="off"
              name="username"
              key="username"
              ref={register({ required: "Field is required" })}
              onChange={handleInputChange}
              defaultValue={inputs.username}
              placeholder="User Email Address"
              className={cStyles.input}
            />
          </Form.Field>
        </Form.Group>

        <span
          style={{ flexGrow: 1, display: "flex", justifyContent: "flex-end" }}
        >
          <Button
            primary
            className={cStyles.buttonPrime}
            type="submit"
            loading={loading}
            disabled={loading}
          >
            <Ico name="search" style={{ marginRight: "10px" }} />
            Search Users
          </Button>
        </span>
      </Form>

      <UsersList
        usersData={usersData && usersData.users}
        onRefresh={searchUsers}
        ro={ro}
        showSelectedOrg={showSelectedOrg}
      />

      {message && (
        <ConfirmationDialog
          title={message.title}
          message={message.message}
          onOk={() => setMessage(null)}
        />
      )}
    </Segment>
  );
};

export default Users;
